import React, { useEffect, useState } from 'react';
import { FiRefreshCw, FiBarChart2, FiSettings, FiEye, FiEyeOff } from 'react-icons/fi';
import { useToast } from '../../context/ToastContext';
import '../../styles/Dialer.css';
import '../../styles/CommandCenterSidebar.css';

interface TwilioSettings {
  accountSid: string;
  authToken: string;
  apiKey: string;
  apiSecret: string;
  appSid: string;
}

const EMPTY_SETTINGS: TwilioSettings = { accountSid: '', authToken: '', apiKey: '', apiSecret: '', appSid: '' };

const FIELDS: Array<{ key: keyof TwilioSettings; label: string; placeholder: string }> = [
  { key: 'accountSid', label: 'Account Sid', placeholder: 'Enter Account Sid…' },
  { key: 'authToken', label: 'Auth Token', placeholder: 'Enter Auth Token…' },
  { key: 'apiKey', label: 'API Key', placeholder: 'Enter API Key…' },
  { key: 'apiSecret', label: 'API Secret', placeholder: 'Enter API Secret…' },
  { key: 'appSid', label: 'App Sid', placeholder: 'Enter App Sid…' },
];

type SidebarTab = 'analytics' | 'config';

interface DialerStats {
  totalCalls: number;
  completedCalls: number;
  incompleteCalls: number;
  transcribedCalls: number;
  avgDurationSeconds: number | null;
  totalSms: number;
}

function formatAvgDuration(seconds: number | null): string {
  if (!seconds) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function pct(count: number, total: number): number {
  return total > 0 ? Math.round((count / total) * 100) : 0;
}

const DialerSidebarContent: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState<SidebarTab>('analytics');

  const [stats, setStats] = useState<DialerStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [settings, setSettings] = useState<TwilioSettings>(EMPTY_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [visibleFields, setVisibleFields] = useState<Set<keyof TwilioSettings>>(new Set());

  const toggleFieldVisibility = (key: keyof TwilioSettings) => {
    setVisibleFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    const authToken = localStorage.getItem('authToken') || '';
    fetch(`${(process.env.REACT_APP_DataFabric_Url || 'https://datafabricapi-stage.insurancegig.com')}/api/twilio/analytics`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => setStats(data?.data ?? null))
      .catch(() => setStats(null))
      .finally(() => setStatsLoading(false));
  }, []);

  useEffect(() => {
    const toolboxToken = localStorage.getItem('toolboxToken') || '';
    fetch(`${(process.env.REACT_APP_DataFabric_Url || 'https://datafabricapi-stage.insurancegig.com')}/api/twilio/settings`, {
      headers: { Authorization: `Bearer ${toolboxToken}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const d = data?.data ?? data;
        if (d) {
          setSettings({
            accountSid: d.accountSid || '',
            authToken: d.authToken || '',
            apiKey: d.apiKey || '',
            apiSecret: d.apiSecret || '',
            appSid: d.appSid || '',
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (key: keyof TwilioSettings, value: string) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const allFieldsFilled = FIELDS.every((f) => settings[f.key].trim() !== '');

  const handleSave = () => {
    if (!allFieldsFilled) {
      showError('All fields are required — please fill in every Twilio setting before saving.');
      return;
    }

    setSaving(true);
    const toolboxToken = localStorage.getItem('toolboxToken') || '';
    fetch(`${(process.env.REACT_APP_DataFabric_Url || 'https://datafabricapi-stage.insurancegig.com')}/api/twilio/settings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${toolboxToken}` },
      body: JSON.stringify(settings),
    })
      .then(async (r) => {
        const data = await r.json().catch(() => null);
        if (!r.ok || data?.success === false) throw new Error(data?.errorMessage || 'Failed to save settings. Please try again.');
        return data;
      })
      .then(() => showSuccess('Settings saved.'))
      .catch((err) => showError(err?.message || 'Failed to save settings. Please try again.'))
      .finally(() => setSaving(false));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* ── Tab bar — matches Command Center's sidebar tab track ── */}
      <div className="cc-sb-tabs">
        <div className="cc-sb-tab-track">
          <button
            className={`cc-sb-tab${activeTab === 'analytics' ? ' cc-sb-tab--active cc-sb-tab--active-dashboard' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            <FiBarChart2 size={11} />
            Analytics
          </button>
          <button
            className={`cc-sb-tab${activeTab === 'config' ? ' cc-sb-tab--active cc-sb-tab--active-config' : ''}`}
            onClick={() => setActiveTab('config')}
          >
            <FiSettings size={11} />
            Configuration
          </button>
        </div>
      </div>

      {/* ── Analytics tab — lifetime, unfiltered totals ── */}
      {activeTab === 'analytics' && (
        <div className="cc-sb-dashboard">
          {statsLoading ? (
            <div className="dialer-empty">Loading analytics…</div>
          ) : (
            <>
              <div className="cc-sb-stats">
                {[
                  { cls: 'cc-sb-stat--amber', lbl: 'TOTAL CALLS', ctx: 'All time', val: stats ? stats.totalCalls : '—', numCls: 'cc-sb-stat__num--amber' },
                  { cls: 'cc-sb-stat--green', lbl: 'COMPLETED', ctx: 'Successfully connected', val: stats ? stats.completedCalls : '—', numCls: 'cc-sb-stat__num--green' },
                  { cls: 'cc-sb-stat--purple', lbl: 'AVG DURATION', ctx: 'Per completed call (m:ss)', val: stats ? formatAvgDuration(stats.avgDurationSeconds) : '—', numCls: 'cc-sb-stat__num--purple' },
                  { cls: 'cc-sb-stat--amber', lbl: 'TOTAL SMS', ctx: 'All time', val: stats ? stats.totalSms : '—', numCls: 'cc-sb-stat__num--amber' },
                ].map((s, i) => (
                  <div key={i} className={`cc-sb-stat ${s.cls}`}>
                    <div className="cc-sb-stat__body">
                      <span className="cc-sb-stat__label">{s.lbl}</span>
                      <span className="cc-sb-stat__context">{s.ctx}</span>
                    </div>
                    <span className={`cc-sb-stat__num ${s.numCls}`}>{s.val}</span>
                  </div>
                ))}
              </div>

              {/* Completed vs. incomplete — not a real answered/busy/no-answer disposition
                  (Twilio's CallStatus here only reflects the recording's own status), so
                  labeled honestly as "incomplete" rather than implying a failure reason. */}
              <div className="cc-sb-workload">
                <div className="cc-sb-section-lbl">Call Outcomes</div>
                <div className="cc-sb-split-card">
                  <div className="dialer-outcome-rows">
                    {[
                      { label: 'Completed', count: stats?.completedCalls ?? 0, color: '#4ade80' },
                      { label: 'Incomplete', count: stats?.incompleteCalls ?? 0, color: '#9ca3af' },
                    ].map((row) => (
                      <div className="dialer-outcome-row" key={row.label}>
                        <span className="dialer-outcome-dot" style={{ background: row.color }} />
                        <span className="dialer-outcome-label">{row.label}</span>
                        <div className="dialer-outcome-bar-bg">
                          <div
                            className="dialer-outcome-bar-fill"
                            style={{ width: `${pct(row.count, stats?.totalCalls ?? 0)}%`, background: row.color }}
                          />
                        </div>
                        <span className="dialer-outcome-count" style={{ color: row.color }}>{row.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* AI transcript coverage — % of all calls that got a speech-to-text transcript */}
              <div className="cc-sb-workload">
                <div className="cc-sb-section-lbl">AI Transcript Coverage</div>
                <div className="cc-sb-split-card">
                  <div className="dialer-outcome-bar-bg dialer-outcome-bar-bg--lg">
                    <div
                      className="dialer-outcome-bar-fill"
                      style={{ width: `${pct(stats?.transcribedCalls ?? 0, stats?.totalCalls ?? 0)}%`, background: '#a78bfa' }}
                    />
                  </div>
                  <p className="dialer-outcome-coverage-note">
                    {stats?.transcribedCalls ?? 0} of {stats?.totalCalls ?? 0} calls transcribed ({pct(stats?.transcribedCalls ?? 0, stats?.totalCalls ?? 0)}%)
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Configuration tab — existing Twilio settings form ── */}
      {activeTab === 'config' && (
        <div className="cc-sb-dashboard">
          <div className="cc-sb-section-lbl">Twilio Settings</div>
          <div className="dialer-settings-card">
            {loading ? (
              <div className="dialer-empty">Loading settings…</div>
            ) : (
              <>
                {FIELDS.map((f) => {
                  const visible = visibleFields.has(f.key);
                  return (
                    <div className="dialer-settings-field" key={f.key}>
                      <label>{f.label} <span className="dialer-settings-required">*</span></label>
                      <div className="dialer-settings-field-input">
                        <input
                          type={visible ? 'text' : 'password'}
                          className="dialer-input dialer-settings-input"
                          placeholder={f.placeholder}
                          value={settings[f.key]}
                          onChange={(e) => handleChange(f.key, e.target.value)}
                          autoComplete="off"
                          required
                        />
                        <button
                          type="button"
                          className="dialer-settings-visibility-btn"
                          onClick={() => toggleFieldVisibility(f.key)}
                          title={visible ? 'Hide value' : 'Show value'}
                        >
                          {visible ? <FiEyeOff size={13} /> : <FiEye size={13} />}
                        </button>
                      </div>
                    </div>
                  );
                })}

                <button className="dialer-call-btn dialer-settings-save-btn" onClick={handleSave} disabled={saving || !allFieldsFilled} title={!allFieldsFilled ? 'All fields are required' : undefined}>
                  {saving ? <FiRefreshCw size={13} className="dialer-spin" /> : null}
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DialerSidebarContent;

